// src/App.jsx — BuildMyShifts main application
import './i18n';
import { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { doc, onSnapshot, updateDoc } from 'firebase/firestore';
import { auth, db } from './firebase';

// Auth pages
import Login from './Login';
import Register from './Register';
import ForgotPassword from './ForgotPassword';
import SplashScreen from './SplashScreen';

// Main app pages
import Dashboard from './Dashboard';
import RotaManager from './RotaManager';
import StaffManager from './StaffManager';
import GroupsManager from './GroupsManager';
import TimeClock from './TimeClock';
import PayrollExport from './PayrollExport';
import AvailabilityManager from './AvailabilityManager';
import Analytics from './pages/Analytics';
import StaffDashboard from './pages/StaffDashboard';
import SwapBoard from './pages/SwapBoard';
import Reports from './pages/Reports';
import NotificationsCentre from './pages/NotificationsCentre';
import AuditLogViewer from './pages/AuditLogViewer';
import HowToGuides from './pages/HowToGuides';
import PoliciesProcedures from './pages/PoliciesProcedures';
import KnowledgeHub from './pages/KnowledgeHub';

// Subscription pages
import LandingPage from './pages/subscription/LandingPage';
import PublicNavbar from './components/PublicNavbar';
import Footer from './components/Footer';
import CompanySetup from './pages/subscription/CompanySetup';
import TrialSuccess from './pages/subscription/TrialSuccess';
import Checkout from './pages/subscription/Checkout';
import CheckoutSuccess from './pages/subscription/CheckoutSuccess';
import SubscriptionManagement from './pages/subscription/SubscriptionManagement';
import SubscriptionLocked from './pages/subscription/SubscriptionLocked';
import UserManagement from './pages/subscription/UserManagement';
import Features from './pages/subscription/Features';
import Pricing from './pages/subscription/Pricing';
import Contact from './pages/subscription/Contact';
import ClockingMachineMode from './pages/subscription/ClockingMachineMode';
import TermsOfService from './pages/subscription/TermsOfService';
import PrivacyPolicy from './pages/subscription/PrivacyPolicy';
import RefundPolicy from './pages/subscription/RefundPolicy';
import DeleteAccount from './pages/subscription/DeleteAccount';

// Components
import Navbar from './components/Navbar';
import RequirePermission from './components/RequirePermission';
import LanguageSwitcher from './components/LanguageSwitcher';

// Settings
import Settings from './Settings';

const PENDING_SHIFTS_COMPANY_KEY = 'buildmyshifts.pendingCompanySetup';

function getPendingCompanyForUser(uid) {
  try {
    const pending = JSON.parse(window.localStorage.getItem(PENDING_SHIFTS_COMPANY_KEY) || 'null');
    return pending?.uid === uid ? pending.companyId : null;
  } catch {
    return null;
  }
}

function setPendingCompanyForUser(uid, companyId) {
  try {
    window.localStorage.setItem(PENDING_SHIFTS_COMPANY_KEY, JSON.stringify({ uid, companyId }));
  } catch {
    // Local storage is only a handoff guard; Firestore remains the source of truth.
  }
}

function clearPendingCompanyForUser(uid) {
  try {
    const pending = JSON.parse(window.localStorage.getItem(PENDING_SHIFTS_COMPANY_KEY) || 'null');
    if (pending?.uid === uid) {
      window.localStorage.removeItem(PENDING_SHIFTS_COMPANY_KEY);
    }
  } catch {
    window.localStorage.removeItem(PENDING_SHIFTS_COMPANY_KEY);
  }
}

export default function App() {
  const [user, setUser] = useState(null);
  const [loadingSplash, setLoadingSplash] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [hasShownLoginSplash, setHasShownLoginSplash] = useState(false);
  const [userRole, setUserRole] = useState(null);
  const [companyId, setCompanyId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userData, setUserData] = useState(null);
  const [companyData, setCompanyData] = useState(null);

  useEffect(() => {
    document.documentElement.classList.remove('dark');
  }, []);

  useEffect(() => {
    let unsubscribeSnapshot = null;

    const unsub = onAuthStateChanged(auth, (u) => {
      if (!isLoggingOut) {
        if (!user && u && !hasShownLoginSplash) {
          setLoadingSplash(true);
          setHasShownLoginSplash(true);
          setTimeout(() => setLoadingSplash(false), 3500);
        }
        setUser(u);

        if (unsubscribeSnapshot) {
          unsubscribeSnapshot();
          unsubscribeSnapshot = null;
        }

        if (u) {
          const userDocRef = doc(db, 'users', u.uid);
          unsubscribeSnapshot = onSnapshot(userDocRef, async (userDoc) => {
            if (userDoc.exists()) {
              const data = userDoc.data();

              // Read BMS-namespaced fields; fall back for legacy BMS-only users
              let bmsRole = data.shiftsRole || null;
              const pendingCompanyId = getPendingCompanyForUser(u.uid);
              let bmsCompanyId = data.shiftsCompanyId || pendingCompanyId;

              // Auto-migrate legacy BMS users (have product:'buildmyshifts' but no shiftsRole)
              if (!bmsRole && data.product === 'buildmyshifts' && data.role) {
                bmsRole = data.role;
                bmsCompanyId = data.companyId || null;
                // Write namespaced fields so future reads don't need fallback
                try {
                  await updateDoc(userDocRef, {
                    shiftsRole: bmsRole,
                    shiftsCompanyId: bmsCompanyId,
                  });
                } catch (migErr) {
                  console.warn('Auto-migrate BMS user fields failed:', migErr);
                }
              }

              if (data.shiftsCompanyId) {
                clearPendingCompanyForUser(u.uid);
              }

              if (pendingCompanyId && !data.shiftsCompanyId && !bmsRole) {
                bmsRole = 'admin';
              }

              const checksCompanyId = data.companyId || data.checksCompanyId || null;
              const assignedGroupIds = data.shiftsAssignedGroupIds || data.assignedGroupIds || [];
              setCompanyId(bmsCompanyId);
              setUserRole(bmsRole);
              // Expose BMS-resolved role/companyId while preserving BMC company for shared policies.
              setUserData({
                ...data,
                role: bmsRole,
                companyId: bmsCompanyId,
                shiftsCompanyId: bmsCompanyId,
                checksCompanyId,
                policyCompanyId: checksCompanyId || bmsCompanyId,
                assignedGroupIds,
              });
            } else {
              const pendingCompanyId = getPendingCompanyForUser(u.uid);
              if (pendingCompanyId) {
                setCompanyId(pendingCompanyId);
                setUserRole('admin');
                setUserData({
                  email: u.email || '',
                  displayName: u.displayName || '',
                  role: 'admin',
                  shiftsRole: 'admin',
                  companyId: pendingCompanyId,
                  shiftsCompanyId: pendingCompanyId,
                  policyCompanyId: pendingCompanyId,
                  assignedGroupIds: [],
                });
              } else {
                setCompanyId(null);
                setUserRole(null);
                setUserData(null);
                setCompanyData(null);
              }
            }
            setLoading(false);
          }, (error) => {
            console.error('Error listening to user profile:', error);
            setCompanyId(null);
            setUserRole(null);
            setUserData(null);
            setLoading(false);
          });
        } else {
          setUserRole(null);
          setCompanyId(null);
          setUserData(null);
          setLoading(false);
        }
      }
    });

    const handleLogout = async () => {
      setIsLoggingOut(true);
      setTimeout(async () => {
        await signOut(auth);
        setIsLoggingOut(false);
        setUser(null);
      }, 3500);
    };
    window.addEventListener('logout', handleLogout);

    return () => {
      unsub();
      if (unsubscribeSnapshot) unsubscribeSnapshot();
      window.removeEventListener('logout', handleLogout);
    };
  }, [isLoggingOut, user, hasShownLoginSplash]);

  const pendingCompanyIdForRender = user ? getPendingCompanyForUser(user.uid) : null;
  const activeCompanyId = companyId || pendingCompanyIdForRender;
  const activeUserRole = userRole || (pendingCompanyIdForRender ? 'admin' : null);
  const activeUserData = activeCompanyId
    ? {
        ...(userData || {}),
        email: userData?.email || user?.email || '',
        displayName: userData?.displayName || user?.displayName || '',
        role: userData?.role || activeUserRole,
        shiftsRole: userData?.shiftsRole || activeUserRole,
        companyId: activeCompanyId,
        shiftsCompanyId: activeCompanyId,
        policyCompanyId: userData?.policyCompanyId || userData?.checksCompanyId || activeCompanyId,
        assignedGroupIds: userData?.assignedGroupIds || [],
      }
    : userData;

  // Listen to company doc for subscription state
  useEffect(() => {
    if (!activeCompanyId) {
      setCompanyData(null);
      return;
    }
    const unsub = onSnapshot(doc(db, 'companies', activeCompanyId), (snap) => {
      setCompanyData(snap.exists() ? snap.data() : null);
    });
    return () => unsub();
  }, [activeCompanyId]);

  const isSubscriptionLocked = companyData &&
    ['locked', 'cancelled', 'canceled', 'past_due'].includes(companyData.shiftsSubscriptionStatus);

  const handleCompanySetupComplete = (newCompanyId) => {
    setPendingCompanyForUser(user.uid, newCompanyId);
    setCompanyId(newCompanyId);
    setUserRole('admin');
    setUserData((prev) => ({
      ...(prev || {}),
      email: prev?.email || user?.email || '',
      displayName: prev?.displayName || user?.displayName || '',
      role: 'admin',
      shiftsRole: 'admin',
      companyId: newCompanyId,
      shiftsCompanyId: newCompanyId,
      policyCompanyId: prev?.checksCompanyId || newCompanyId,
      assignedGroupIds: prev?.assignedGroupIds || [],
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-950 via-slate-900 to-slate-800">
        <div className="text-xl text-gray-400">Loading...</div>
      </div>
    );
  }

  if (loadingSplash) return <SplashScreen mode="on" onFinish={() => setLoadingSplash(false)} />;
  if (isLoggingOut) return <SplashScreen mode="off" onFinish={() => setIsLoggingOut(false)} />;

  // Public routes (not authenticated)
  if (!user) {
    return (
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/features" element={<Features />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/clocking-machine-mode" element={
            <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-800">
              <PublicNavbar />
              <ClockingMachineMode />
              <Footer />
            </div>
          } />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/refund-policy" element={<RefundPolicy />} />
          <Route path="/delete-account" element={<DeleteAccount />} />
          <Route path="/login" element={<Login onLogin={(u) => setUser(u)} />} />
          <Route path="/register" element={<Register onRegister={(u) => setUser(u)} />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/guide" element={
            <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-800">
              <PublicNavbar />
              <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                <HowToGuides />
              </main>
              <Footer />
            </div>
          } />
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </BrowserRouter>
    );
  }

  // User without BMS company — setup flow (includes cross-platform users from BMC)
  if (!activeCompanyId) {
    return (
      <BrowserRouter>
        <Routes>
          <Route path="/setup" element={
            <CompanySetup
              user={user}
              profile={userData}
              onComplete={handleCompanySetupComplete}
            />
          } />
          <Route path="/trial-success" element={<TrialSuccess />} />
          <Route path="/login" element={<Navigate to="/setup" />} />
          <Route path="/register" element={<Navigate to="/setup" />} />
          <Route path="*" element={<Navigate to="/setup" />} />
        </Routes>
      </BrowserRouter>
    );
  }

  // Authenticated routes
  return (
    <BrowserRouter>
      <Routes>
        {/* Public info pages — rendered WITHOUT dashboard shell so they keep their own navbar/footer */}
        <Route path="/features" element={<Features />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/clocking-machine-mode" element={
          <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-800">
            <PublicNavbar />
            <ClockingMachineMode />
            <Footer />
          </div>
        } />
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/refund-policy" element={<RefundPolicy />} />
        <Route path="/delete-account" element={<DeleteAccount />} />

        {/* All other routes get the dashboard shell */}
        <Route path="*" element={
          <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-800 text-gray-100">
            <Navbar user={user} userRole={activeUserRole} userData={activeUserData} companyId={activeCompanyId} />
            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
              <Routes>
                {/* Always accessible — subscription management */}
                <Route path="/subscription" element={
                  <RequirePermission userData={activeUserData} permission="manage_subscription">
                    <SubscriptionManagement user={user} companyId={activeCompanyId} />
                  </RequirePermission>
                } />
                <Route path="/checkout" element={<Checkout user={user} companyId={activeCompanyId} />} />
                <Route path="/checkout-success" element={<CheckoutSuccess companyId={activeCompanyId} />} />
                <Route path="/trial-success" element={<TrialSuccess />} />

                {isSubscriptionLocked ? (
                  <Route path="*" element={<SubscriptionLocked companyData={companyData} companyId={activeCompanyId} />} />
                ) : (
                  <>
                <Route path="/" element={<Navigate to="/dashboard" />} />
                <Route path="/dashboard" element={<Dashboard user={user} userData={activeUserData} companyId={activeCompanyId} />} />
                <Route path="/rotas" element={
                  <RequirePermission userData={activeUserData} permission="view_rotas">
                    <RotaManager user={user} userData={activeUserData} companyId={activeCompanyId} />
                  </RequirePermission>
                } />
                <Route path="/availability" element={
                  <AvailabilityManager user={user} userData={activeUserData} companyId={activeCompanyId} />
                } />
                <Route path="/swaps" element={<Navigate to="/swap-board" replace />} />
                <Route path="/swap-board" element={
                  <SwapBoard user={user} userData={activeUserData} companyId={activeCompanyId} />
                } />
                <Route path="/my-dashboard" element={
                  <StaffDashboard user={user} userData={activeUserData} companyId={activeCompanyId} />
                } />
                <Route path="/analytics" element={
                  <RequirePermission userData={activeUserData} permission="view_analytics">
                    <Analytics user={user} userData={activeUserData} companyId={activeCompanyId} />
                  </RequirePermission>
                } />
                <Route path="/staff" element={
                  <RequirePermission userData={activeUserData} permission="manage_users">
                    <StaffManager user={user} userData={activeUserData} companyId={activeCompanyId} companyData={companyData} />
                  </RequirePermission>
                } />
                <Route path="/groups" element={
                  <RequirePermission userData={activeUserData} permission="manage_groups">
                    <GroupsManager user={user} userData={activeUserData} companyId={activeCompanyId} />
                  </RequirePermission>
                } />
                <Route path="/timeclock" element={
                  <TimeClock user={user} userData={activeUserData} companyId={activeCompanyId} />
                } />
                <Route path="/payroll" element={
                  <RequirePermission userData={activeUserData} permission="view_analytics">
                    <PayrollExport user={user} userData={activeUserData} companyId={activeCompanyId} />
                  </RequirePermission>
                } />
                <Route path="/users" element={
                  <RequirePermission userData={activeUserData} permission="manage_users">
                    <UserManagement user={user} companyId={activeCompanyId} />
                  </RequirePermission>
                } />
                <Route path="/settings" element={
                  <RequirePermission userData={activeUserData} permission="configure_settings">
                    <Settings user={user} userData={activeUserData} companyId={activeCompanyId} />
                  </RequirePermission>
                } />
                <Route path="/reports" element={
                  <RequirePermission userData={activeUserData} permission="view_analytics">
                    <Reports user={user} userData={activeUserData} companyId={activeCompanyId} />
                  </RequirePermission>
                } />
                <Route path="/notifications" element={
                  <NotificationsCentre user={user} userData={activeUserData} companyId={activeCompanyId} />
                } />
                <Route path="/policies" element={<PoliciesProcedures />} />
                <Route path="/knowledge-hub" element={<KnowledgeHub />} />
                <Route path="/audit-log" element={
                  <RequirePermission userData={activeUserData} permission="configure_settings">
                    <AuditLogViewer user={user} userData={activeUserData} companyId={activeCompanyId} />
                  </RequirePermission>
                } />
                <Route path="/guide" element={
                  <HowToGuides />
                } />
                <Route path="*" element={<Navigate to="/dashboard" />} />
                  </>
                )}
              </Routes>
            </main>
            <Footer />
            <div className="fixed bottom-4 right-4 z-50">
              <LanguageSwitcher dropUp variant="dark" />
            </div>
          </div>
        } />
      </Routes>
    </BrowserRouter>
  );
}
